import {
  Component,
  forwardRef,
  Input,
  OnInit,
  ViewChild,
  ElementRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { all, CountryData } from "country-codes-list";
import {
  parsePhoneNumber,
  formatIncompletePhoneNumber,
  PhoneNumber,
  CountryCode,
} from "libphonenumber-js";

@Component({
  selector: "app-phone-input",
  templateUrl: "./phone-input.component.html",
  styleUrls: ["./phone-input.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class PhoneInputComponent implements OnInit, ControlValueAccessor {
  @Input() placeholder = "";
  @Input() disabled = false;
  @Input() required = false;
  @Input() label = "";
  @Input() hint = "";

  @ViewChild("phoneInput", { static: false }) phoneInput!: ElementRef;

  countries: CountryData[] = all().sort((a, b) =>
    a.countryNameLocal.localeCompare(b.countryNameLocal),
  );
  selectedCountry: CountryData | null = null;
  displayValue = "";

  private onChange: (value: string) => void = () => {};
  private onTouched = () => {};

  ngOnInit(): void {
    this.selectedCountry =
      this.countries.find((country) => country.countryCode === "PL") ||
      this.countries[0];
  }

  onPhoneNumberChange(value: string): void {
    if (!value || value.trim() === "") {
      this.setDefaultValue();
    } else {
      this.displayValue = value;
      this.processPhoneNumber(value);
    }
    this.onTouched();
  }

  onBlur(): void {
    if (!this.displayValue || this.displayValue.trim() === "") {
      this.setDefaultValue();
    }
    this.onTouched();
  }

  private setDefaultValue(): void {
    this.displayValue = "+";
    this.selectedCountry = null;
    this.onChange("");
    if (this.phoneInput) {
      (this.phoneInput.nativeElement as HTMLInputElement).value = "+";
    }
  }

  private processPhoneNumber(value: string): void {
    const cleanValue = value.replace(/[^\d+]/g, "");

    if (cleanValue.startsWith("+")) {
      this.detectCountry(cleanValue);
    } else if (cleanValue.length > 0) {
      this.detectCountry("+" + cleanValue);
    }
  }

  private detectCountry(fullNumber: string): void {
    try {
      const parsedNumber = parsePhoneNumber(fullNumber);

      if (parsedNumber?.country) {
        this.setCountryFromParsed(parsedNumber);
      } else if (parsedNumber?.countryCallingCode) {
        this.setCountryFromCallingCode(fullNumber);
      }
    } catch {
      this.setCountryFromCallingCode(fullNumber);
    }
  }

  private setCountryFromParsed(parsedNumber: PhoneNumber): void {
    const country = this.countries.find(
      (c) => c.countryCode === parsedNumber.country,
    );
    if (country) {
      this.selectedCountry = country;
      this.displayValue = parsedNumber.formatInternational();
      this.onChange(this.displayValue);
    }
  }

  private setCountryFromCallingCode(fullNumber: string): void {
    const callingCode = fullNumber.replace("+", "");

    if (callingCode.length < 3) {
      this.displayValue = fullNumber;
      this.onChange(fullNumber);
      return;
    }

    for (let i = 1; i <= 4; i++) {
      const code = callingCode.substring(0, i);
      const country = this.countries.find((c) => c.countryCallingCode === code);

      if (country) {
        this.selectedCountry = country;
        this.formatAndSetValue(fullNumber, country);
        return;
      }
    }

    this.setDefaultValue();
  }

  private formatAndSetValue(fullNumber: string, country: CountryData): void {
    try {
      const formatted = formatIncompletePhoneNumber(
        fullNumber,
        country.countryCode as CountryCode,
      );
      this.displayValue = formatted;
      this.onChange(formatted);
    } catch {
      this.displayValue = fullNumber;
      this.onChange(fullNumber);
    }
  }

  writeValue(value: string): void {
    if (value && value.trim() !== "") {
      this.displayValue = value;
      setTimeout(() => this.processPhoneNumber(value), 0);
    } else {
      this.setDefaultValue();
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  getCountryFlagUrl(alpha2Code: string): string {
    return `https://flagcdn.com/w40/${alpha2Code.toLowerCase()}.png`;
  }
}
